import { AxiosError } from 'axios';
import type {
  AuthFounderAgreementContext,
  AuthFounderAgreementStatus,
  FounderAgreementStatusResponse,
} from '@/types/auth';

export const FOUNDER_AGREEMENT_ACCESS_BLOCKED_EVENT = 'caloriescience:founder-agreement-access-blocked';
export const FOUNDER_AGREEMENT_GATE_STORAGE_KEY = 'caloriescience_founder_agreement_gate';
export const DEFAULT_FOUNDER_AGREEMENT_MESSAGE =
  'Please review and accept the Founding Circle agreement to continue using CalorieScience.';
export const DEFAULT_FOUNDER_AGREEMENT_SUPPORT_EMAIL = 'info@caloriescience.ai';

export interface FounderAgreementGateState {
  accessBlocked: boolean;
  reason: string | null;
  message: string | null;
  supportEmail: string | null;
  founderAgreement: AuthFounderAgreementStatus | null;
  returnTo: string | null;
  updatedAt: string;
}

const isBrowser = () => typeof window !== 'undefined';

const normalizeReason = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

export const normalizeFounderAgreementStatus = (
  value: unknown,
): AuthFounderAgreementStatus | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  return {
    allowed: record.allowed === true,
    accessBlocked: record.accessBlocked === true,
    required: record.required === true,
    isFounder: record.isFounder === true,
    reason: normalizeReason(record.reason) as AuthFounderAgreementStatus['reason'],
    agreementVersion:
      typeof record.agreementVersion === 'string' && record.agreementVersion.trim()
        ? record.agreementVersion
        : 'founding-circle-v1',
    acceptedAt: typeof record.acceptedAt === 'string' ? record.acceptedAt : null,
    acceptedVersion: typeof record.acceptedVersion === 'string' ? record.acceptedVersion : null,
    cohort: typeof record.cohort === 'string' ? record.cohort : null,
    planCode: typeof record.planCode === 'string' ? record.planCode : null,
  };
};

export const parseFounderAgreementStatusResponse = (
  value: unknown,
): FounderAgreementStatusResponse | null => {
  const root = value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  if (!root) {
    return null;
  }

  const payload = (root.data && typeof root.data === 'object'
    ? root.data
    : root) as Record<string, unknown>;
  const founderAgreement = normalizeFounderAgreementStatus(payload.founderAgreement);
  return founderAgreement ? { founderAgreement } : null;
};

export const readFounderAgreementGateState = (): FounderAgreementGateState | null => {
  if (!isBrowser()) {
    return null;
  }

  try {
    const raw = localStorage.getItem(FOUNDER_AGREEMENT_GATE_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<FounderAgreementGateState>;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return {
      accessBlocked: parsed.accessBlocked === true,
      reason: normalizeReason(parsed.reason),
      message: typeof parsed.message === 'string' ? parsed.message : null,
      supportEmail: typeof parsed.supportEmail === 'string' ? parsed.supportEmail : null,
      founderAgreement: normalizeFounderAgreementStatus(parsed.founderAgreement),
      returnTo: typeof parsed.returnTo === 'string' ? parsed.returnTo : null,
      updatedAt:
        typeof parsed.updatedAt === 'string'
          ? parsed.updatedAt
          : new Date().toISOString(),
    };
  } catch {
    return null;
  }
};

export const persistFounderAgreementGateState = (
  input: Partial<FounderAgreementGateState> & { accessBlocked: boolean },
) => {
  if (!isBrowser()) {
    return;
  }

  const previousState = readFounderAgreementGateState();
  const nextState: FounderAgreementGateState = {
    accessBlocked: input.accessBlocked,
    reason: normalizeReason(input.reason) ?? previousState?.reason ?? null,
    message:
      typeof input.message === 'string'
        ? input.message
        : previousState?.message ?? DEFAULT_FOUNDER_AGREEMENT_MESSAGE,
    supportEmail:
      typeof input.supportEmail === 'string'
        ? input.supportEmail
        : previousState?.supportEmail ?? DEFAULT_FOUNDER_AGREEMENT_SUPPORT_EMAIL,
    founderAgreement:
      input.founderAgreement !== undefined
        ? normalizeFounderAgreementStatus(input.founderAgreement)
        : previousState?.founderAgreement ?? null,
    returnTo:
      typeof input.returnTo === 'string'
        ? input.returnTo
        : previousState?.returnTo ?? null,
    updatedAt: new Date().toISOString(),
  };

  localStorage.setItem(FOUNDER_AGREEMENT_GATE_STORAGE_KEY, JSON.stringify(nextState));
};

export const clearFounderAgreementGateState = () => {
  if (!isBrowser()) {
    return;
  }

  localStorage.removeItem(FOUNDER_AGREEMENT_GATE_STORAGE_KEY);
};

const notifyFounderAgreementAccessBlocked = (returnTo: string | null = null) => {
  if (!isBrowser()) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(FOUNDER_AGREEMENT_ACCESS_BLOCKED_EVENT, {
      detail: { returnTo },
    }),
  );
};

const syncBlockedState = (params: {
  reason?: string | null;
  message?: string | null;
  supportEmail?: string | null;
  founderAgreement?: AuthFounderAgreementStatus | null;
  returnTo?: string | null;
}) => {
  persistFounderAgreementGateState({
    accessBlocked: true,
    reason: params.reason ?? params.founderAgreement?.reason ?? 'missing',
    message: params.message || DEFAULT_FOUNDER_AGREEMENT_MESSAGE,
    supportEmail: params.supportEmail || DEFAULT_FOUNDER_AGREEMENT_SUPPORT_EMAIL,
    founderAgreement: params.founderAgreement ?? null,
    returnTo: params.returnTo ?? null,
  });
  notifyFounderAgreementAccessBlocked(params.returnTo ?? null);
};

export const syncFounderAgreementGateFromAuthStatus = (
  context: AuthFounderAgreementContext | null | undefined,
  returnTo: string | null = null,
): boolean => {
  if (!context) {
    return false;
  }

  if (!context.accessBlocked) {
    clearFounderAgreementGateState();
    return false;
  }

  syncBlockedState({
    reason: context.reason,
    message: context.message,
    supportEmail: context.supportEmail,
    founderAgreement: context.founderAgreement,
    returnTo,
  });
  return true;
};

export const syncFounderAgreementGateFromStatusResponse = (
  response: FounderAgreementStatusResponse | null | undefined,
  returnTo: string | null = null,
): boolean => {
  if (!response?.founderAgreement?.accessBlocked) {
    clearFounderAgreementGateState();
    return false;
  }

  syncBlockedState({
    reason: response.founderAgreement.reason,
    founderAgreement: response.founderAgreement,
    returnTo,
  });
  return true;
};

export const isFounderAgreementRequiredApiError = (error: unknown): boolean => {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  if (error.response?.status !== 403) {
    return false;
  }

  const data = error.response.data;
  if (!data || typeof data !== 'object') {
    return false;
  }

  return (data as Record<string, unknown>).error === 'Founder agreement required';
};

export const syncFounderAgreementGateFromApiError = (
  error: unknown,
  returnTo: string | null = null,
): boolean => {
  if (!isFounderAgreementRequiredApiError(error) || !(error instanceof AxiosError)) {
    return false;
  }

  const data = (error.response?.data ?? {}) as Record<string, unknown>;
  const founderAgreement = normalizeFounderAgreementStatus(data.details);
  syncBlockedState({
    reason: founderAgreement?.reason ?? 'missing',
    message: typeof data.message === 'string' ? data.message : null,
    supportEmail: typeof data.supportEmail === 'string' ? data.supportEmail : null,
    founderAgreement,
    returnTo,
  });
  return true;
};
